<template>
    <div>
        <portal-app-header />
        <div v-if="!isLoading" class="flex justify-content-center mt-14 mx-1">
            <div class="col lg:col-6 xl:col-5">
                <p-card v-if="expiredReset" class="form-card">
                    <template #title>
                        Password Reset Expired
                    </template>
                    <template #content>
                        <p>
                            This reset link has expired. Click below to submit a new password reset request.
                        </p>
                    </template>
                    <template #footer>
                        <p-button
                            label="Primary"
                            @click="showModal({ name: 'ResetLoginModal' })"
                        >
                            Reset Password
                        </p-button>
                    </template>
                </p-card>
                <p-card v-else class="form-card">
                    <template #title>
                        Reset Password
                    </template>
                    <template #content>
                        <p>
                            <strong>{{ t('labels.user.securityQuestion') }}:</strong> {{ resetConfirmation.securityQuestion }}
                        </p>
                        <form-field
                            v-slot="{ onBlur }"
                            :validation-state="validationState.result.value.securityQuestionAnswer"
                            label-key="labels.user.securityQuestionAnswer"
                            required
                        >
                            <p-input-text
                                v-model="securityQuestionAnswer"
                                @blur="onBlur"
                            />
                        </form-field>
                        <p-divider />
                        <form-field
                            v-slot="{ onBlur }"
                            :validation-state="validationState.result.value.password"
                            label-key="labels.user.newPassword"
                            required
                        >
                            <p-input-text
                                v-model="password"
                                type="password"
                                @blur="onBlur"
                            />
                        </form-field>
                        <form-field
                            v-slot="{ onBlur }"
                            :validation-state="validationState.result.value.confirmPassword"
                            label-key="labels.user.confirmNewPassword"
                            required
                        >
                            <p-input-text
                                v-model="confirmPassword"
                                type="password"
                                @blur="onBlur"
                            />
                        </form-field>
                    </template>
                    <template #footer>
                        <p-button
                            label="Primary"
                            @click="resetLogin"
                        >
                            Reset
                        </p-button>
                    </template>
                </p-card>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { useRootStore } from '@/store';
import { Alert, Modal } from '@/store/classes';
import {ExternalFacilityType} from "@/store/enums";
import { computed, onMounted, ref } from 'vue';
import axios from "axios";
import { useRouter } from 'vue-router';
import { useFormValidation } from '@/components/common/FormValidation';
import { z } from 'zod';
import { PasswordSchema, RequiredStringSchema } from "@/components/common/schemas";
import PortalAppHeader from '@/components/portal/PortalAppHeader.vue3.vue';
import FormField from "@/components/common/FormField.vue3.vue"
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps<{ resetKey: string }>();

const router = useRouter();
const rootStore = useRootStore();

const showModal = async (modal: Modal) => rootStore.dispatch('showModal', modal);
const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);

const isLoading = ref(true);
const savePending = ref(false);
const expiredReset = ref(false);
const resetConfirmation = ref({
    securityQuestionIndex: undefined,
    securityQuestion: ''
});
const securityQuestionAnswer = ref('');
const password = ref('');
const confirmPassword = ref('');

const passwordResetSchema = z.object({
    securityQuestionAnswer: RequiredStringSchema,
    password: RequiredStringSchema.and(PasswordSchema),
    confirmPassword: RequiredStringSchema,
}).superRefine((val, ctx) => {  
    if (val.password !== val.confirmPassword) {
        ctx.addIssue({
            message: t('message.validation.confirmNewPasswordMatch'),
            code: 'custom',
            path: ['confirmPassword']
        });
    }
});

const validationState = useFormValidation<z.infer<typeof passwordResetSchema>>(
    passwordResetSchema,
    computed(() => ({
        securityQuestionAnswer:securityQuestionAnswer.value,
        password:password.value,
        confirmPassword: confirmPassword.value,
    })),
    false
);

onMounted(async ()=>{
    try {
        await showLoader('reset-login');
        const resetResponse = await axios.get(`/auth/reset/${props.resetKey}`);
        resetConfirmation.value = resetResponse.data;
        if (resetResponse.status === 204) {
            expiredReset.value = true; 
        } else if (resetResponse.status !== 200) {
            throw 'Invalid password reset request';
        }
    } catch (e) {
        console.error(e);
        await showAlert({
            text: 'Unable to find password reset request, please submit a new request',
            type: 'error'
        });
        router.push({
            path: '/login',
            query: {
                facilityType: ExternalFacilityType.NhMessaging
            }
        });
    } finally {
        await hideLoader('reset-login');
        isLoading.value = false;
    }
})

async function resetLogin(): Promise<void> {
    if (savePending.value) return;
    savePending.value = true;
            
    if (!validationState.validate()) {
        savePending.value = false;
        return;
    }
            
    try {
        await showLoader('reset-login');
        await axios.put(`/auth/reset/${props.resetKey}`, {
            securityQuestionIndex: resetConfirmation.value.securityQuestionIndex,
            securityQuestionAnswer: securityQuestionAnswer.value,
            newPassword: password.value 
        });
    } catch (e: any) {
        const text = e.response?.status === 400 && e.response?.data
            ? e.response.data
            : 'Unable to reset password, please try again';
        await showAlert({text, type: 'error'});
        return;
    } finally {
        await hideLoader('reset-login');
        savePending.value = false;
    }
    await showAlert({text: 'Password reset, log in to continue', type: 'success'});
    router.push({
        path: '/login',
        query: {
            facilityType: ExternalFacilityType.NhMessaging
        }
    });
}
</script>
